import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  Copy,
  Play,
  Palette,
  Type,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Loader2,
  FileJson,
  FileCode,
  Eye,
  CreditCard,
  Undo2,
  Redo2,
  Plus,
  Trash2,
  Copy as CopyIcon,
  MoveUp,
  MoveDown,
  Menu,
  PanelLeftClose,
  MoreVertical,
} from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';
import { usePresentationStore } from '../store/presentation';
import { THEME_PRESETS, THEME_CATEGORIES, AVAILABLE_FONTS, SLIDE_TRANSITIONS, TEXT_ANIMATIONS, ELEMENT_ANIMATIONS, DEFAULT_TYPOGRAPHY, DEFAULT_ANIMATIONS } from '../data/themePresets';
import { PRESENTATION_CREDITS, ThemePreset, TypographySettings, AnimationSettings } from '../types';
import FullscreenPresenter from '../components/presentation/FullscreenPresenter';
import AIThemeModal from '../components/presentation/AIThemeModal';
import SlideSettingsPanel from '../components/presentation/SlideSettingsPanel';
import SlideLayoutRenderer from '../components/presentation/SlideLayoutRenderer';
import { Settings } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Animation helper functions for Framer Motion
const getSlideTransitionInitial = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 1 };
    case 'fade': return { opacity: 0 };
    case 'slide': return { opacity: 0, x: 100 };
    case 'zoom': return { opacity: 0, scale: 0.8 };
    case 'flip': return { opacity: 0, rotateY: -90 };
    case 'cube': return { opacity: 0, rotateY: -90, z: -200 };
    case 'dissolve': return { opacity: 0, filter: 'blur(10px)' };
    default: return { opacity: 0 };
  }
};

const getSlideTransitionAnimate = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 1 };
    case 'fade': return { opacity: 1 };
    case 'slide': return { opacity: 1, x: 0 };
    case 'zoom': return { opacity: 1, scale: 1 };
    case 'flip': return { opacity: 1, rotateY: 0 };
    case 'cube': return { opacity: 1, rotateY: 0, z: 0 };
    case 'dissolve': return { opacity: 1, filter: 'blur(0px)' };
    default: return { opacity: 1 };
  }
};

const getSlideTransitionExit = (transition: string) => {
  switch (transition) {
    case 'none': return { opacity: 0 };
    case 'fade': return { opacity: 0 };
    case 'slide': return { opacity: 0, x: -100 };
    case 'zoom': return { opacity: 0, scale: 1.2 };
    case 'flip': return { opacity: 0, rotateY: 90 };
    case 'cube': return { opacity: 0, rotateY: 90, z: -200 };
    case 'dissolve': return { opacity: 0, filter: 'blur(10px)' };
    default: return { opacity: 0 };
  }
};

const getTextAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '0%' };
    case 'fade-in-up': return { opacity: 0, y: 30 };
    case 'fade-in-down': return { opacity: 0, y: -30 };
    case 'bounce-in': return { opacity: 0, scale: 0.3 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    default: return { opacity: 1 };
  }
};

const getTextAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, scale: 1 };
    case 'typewriter': return { opacity: 1, width: '100%' };
    case 'fade-in-up': return { opacity: 1, y: 0 };
    case 'fade-in-down': return { opacity: 1, y: 0 };
    case 'bounce-in': return { opacity: 1, scale: 1 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationInitial = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 0 };
    case 'slide-up': return { opacity: 0, y: 30 };
    case 'slide-left': return { opacity: 0, x: 50 };
    case 'slide-right': return { opacity: 0, x: -50 };
    case 'zoom-in': return { opacity: 0, scale: 0.5 };
    case 'bounce': return { opacity: 0, y: 30 };
    case 'flip': return { opacity: 0, rotateX: -90 };
    default: return { opacity: 1 };
  }
};

const getElementAnimationAnimate = (animation: string) => {
  switch (animation) {
    case 'none': return { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0 };
    case 'fade-in': return { opacity: 1 };
    case 'slide-up': return { opacity: 1, y: 0 };
    case 'slide-left': return { opacity: 1, x: 0 };
    case 'slide-right': return { opacity: 1, x: 0 };
    case 'zoom-in': return { opacity: 1, scale: 1 };
    case 'bounce': return { opacity: 1, y: 0 };
    case 'flip': return { opacity: 1, rotateX: 0 };
    default: return { opacity: 1 };
  }
};

export default function PresentationBuilder() {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const { token, user } = useAuthStore();
  const {
    currentPresentation,
    isLoading,
    isSaving,
    error,
    selectedSlideIndex,
    activePanel,
    loadPresentation,
    setPresentation,
    applyTheme,
    applyTypography,
    applyAnimations,
    selectSlide,
    setActivePanel,
    exportJSON,
    exportHTML,
    setError,
    duplicateSlide,
    deleteSlide,
    moveSlide,
    addSlide,
    undo,
    redo,
    canUndo,
    canRedo,
  } = usePresentationStore();

  const [copiedJSON, setCopiedJSON] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showAIThemeModal, setShowAIThemeModal] = useState(false);
  const [editingSlideIndex, setEditingSlideIndex] = useState<number | null>(null);
  const [showCreditConfirm, setShowCreditConfirm] = useState<{
    type: 'theme' | 'typography' | 'animations';
    data: unknown;
    credits: number;
    description: string;
  } | null>(null);

  // Mobile responsive state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showMobileExportMenu, setShowMobileExportMenu] = useState(false);

  // Local state for previewing changes before applying
  const [previewTheme, setPreviewTheme] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [tempTypography, setTempTypography] = useState<TypographySettings>(DEFAULT_TYPOGRAPHY);
  const [tempAnimations, setTempAnimations] = useState<AnimationSettings>(DEFAULT_ANIMATIONS);

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (presentationId && token) {
      loadPresentation(presentationId);
    }
  }, [presentationId, token, loadPresentation]);

  useEffect(() => {
    if (currentPresentation?.customization) {
      setTempTypography(currentPresentation.customization.typography || DEFAULT_TYPOGRAPHY);
      setTempAnimations(currentPresentation.customization.animations || DEFAULT_ANIMATIONS);
    }
  }, [currentPresentation]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const handleApplyTheme = (themeId: string) => {
    const theme = THEME_PRESETS.find(t => t.id === themeId);
    if (!theme) return;

    setShowCreditConfirm({
      type: 'theme',
      data: themeId,
      credits: PRESENTATION_CREDITS.THEME_CHANGE,
      description: `Apply "${theme.name}" theme`,
    });
  };

  const handleApplyTypography = () => {
    setShowCreditConfirm({
      type: 'typography',
      data: tempTypography,
      credits: PRESENTATION_CREDITS.TYPOGRAPHY_CHANGE,
      description: 'Apply typography changes',
    });
  };

  const handleApplyAnimations = () => {
    setShowCreditConfirm({
      type: 'animations',
      data: tempAnimations,
      credits: PRESENTATION_CREDITS.ANIMATION_CHANGE,
      description: 'Apply animation settings',
    });
  };

  const confirmAction = async () => {
    if (!showCreditConfirm) return;

    let success = false;
    switch (showCreditConfirm.type) {
      case 'theme':
        success = await applyTheme(showCreditConfirm.data as string);
        break;
      case 'typography':
        success = await applyTypography(showCreditConfirm.data as TypographySettings);
        break;
      case 'animations':
        success = await applyAnimations(showCreditConfirm.data as AnimationSettings);
        break;
    }

    if (success) {
      setPreviewTheme(null);
    }
    setShowCreditConfirm(null);
  };

  const handleExportJSON = () => {
    const json = exportJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPresentation?.title || 'presentation'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportHTML = () => {
    const html = exportHTML();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPresentation?.title || 'presentation'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyJSON = () => {
    navigator.clipboard.writeText(exportJSON());
    setCopiedJSON(true);
    setTimeout(() => setCopiedJSON(false), 2000);
  };

  const getActiveTheme = (): ThemePreset | undefined => {
    if (previewTheme) return THEME_PRESETS.find(t => t.id === previewTheme);
    if (currentPresentation?.customization?.themePreset) {
      return THEME_PRESETS.find(t => t.id === currentPresentation.customization?.themePreset);
    }
    return THEME_PRESETS[0]; // Default to first theme
  };

  const activeTheme = getActiveTheme();
  const filteredThemes = selectedCategory === 'all'
    ? THEME_PRESETS
    : THEME_PRESETS.filter(t => t.category === selectedCategory);

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <div className="text-center">
          <Loader2 className={`w-8 h-8 animate-spin mx-auto mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`} />
          <p className={isDark ? 'text-text-secondary' : 'text-slate-600'}>Loading presentation...</p>
        </div>
      </div>
    );
  }

  if (!currentPresentation) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <div className="text-center">
          <p className={`text-lg mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Presentation not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentSlide = currentPresentation.slides?.[selectedSlideIndex];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-100'}`}>
      {/* Top Toolbar */}
      <div className={`h-14 sm:h-16 border-b flex items-center justify-between px-2 sm:px-4 ${
        isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200'
      }`}>
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {/* Mobile sidebar toggle */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
            }`}
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-lg transition-colors hidden sm:flex ${
              isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className={`font-semibold truncate text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {currentPresentation.title}
            </h1>
            <p className={`text-xs truncate hidden sm:block ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              {currentPresentation.slides?.length || 0} slides • {currentPresentation.style}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2">
          {/* Undo/Redo - always visible */}
          <div className="flex items-center gap-0.5 sm:gap-1">
            <button
              onClick={undo}
              disabled={!canUndo()}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors disabled:opacity-30 ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              onClick={redo}
              disabled={!canRedo()}
              className={`p-1.5 sm:p-2 rounded-lg transition-colors disabled:opacity-30 ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
              title="Redo (Ctrl+Y)"
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>

          <div className={`w-px h-6 hidden sm:block ${isDark ? 'bg-dark-600' : 'bg-slate-200'}`} />

          {/* Credits display - hidden on small mobile */}
          <div className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm hidden xs:flex items-center gap-1 sm:gap-2 ${
            isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'
          }`}>
            <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">{user?.credits_remaining || 0} credits</span>
            <span className="sm:hidden">{user?.credits_remaining || 0}</span>
          </div>

          {/* Preview button - always visible */}
          <button
            onClick={() => setIsPreviewMode(true)}
            className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
            }`}
            title="Preview"
          >
            <Eye className="w-5 h-5" />
          </button>

          {/* Desktop export buttons */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={handleCopyJSON}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
              title="Copy JSON"
            >
              {copiedJSON ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
            </button>

            <button
              onClick={handleExportJSON}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
              title="Export JSON"
            >
              <FileJson className="w-5 h-5" />
            </button>

            <button
              onClick={handleExportHTML}
              className="px-3 lg:px-4 py-2 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-lg font-medium text-sm flex items-center gap-2 hover:shadow-lg transition-shadow"
            >
              <FileCode className="w-4 h-4" />
              <span className="hidden lg:inline">Export HTML</span>
              <span className="lg:hidden">Export</span>
            </button>
          </div>

          {/* Mobile export menu */}
          <div className="relative md:hidden">
            <button
              onClick={() => setShowMobileExportMenu(!showMobileExportMenu)}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              }`}
            >
              <MoreVertical className="w-5 h-5" />
            </button>

            <AnimatePresence>
              {showMobileExportMenu && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMobileExportMenu(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className={`absolute right-0 top-full mt-2 w-48 rounded-xl shadow-xl z-50 overflow-hidden ${
                      isDark ? 'bg-dark-800 border border-dark-600' : 'bg-white border border-slate-200'
                    }`}
                  >
                    <button
                      onClick={() => { handleCopyJSON(); setShowMobileExportMenu(false); }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 ${
                        isDark ? 'hover:bg-dark-700 text-white' : 'hover:bg-slate-50 text-slate-900'
                      }`}
                    >
                      <Copy className="w-4 h-4" />
                      Copy JSON
                    </button>
                    <button
                      onClick={() => { handleExportJSON(); setShowMobileExportMenu(false); }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 ${
                        isDark ? 'hover:bg-dark-700 text-white' : 'hover:bg-slate-50 text-slate-900'
                      }`}
                    >
                      <FileJson className="w-4 h-4" />
                      Export JSON
                    </button>
                    <button
                      onClick={() => { handleExportHTML(); setShowMobileExportMenu(false); }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 ${
                        isDark ? 'hover:bg-dark-700 text-white' : 'hover:bg-slate-50 text-slate-900'
                      }`}
                    >
                      <FileCode className="w-4 h-4" />
                      Export HTML
                    </button>
                    <div className={`border-t ${isDark ? 'border-dark-600' : 'border-slate-200'}`} />
                    <button
                      onClick={() => { navigate(-1); setShowMobileExportMenu(false); }}
                      className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 ${
                        isDark ? 'hover:bg-dark-700 text-white' : 'hover:bg-slate-50 text-slate-900'
                      }`}
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-56px)] sm:h-[calc(100vh-64px)]">
        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Left Sidebar - Customization Panels */}
        <motion.div
          className={`
            fixed lg:relative inset-y-0 left-0 z-40 lg:z-auto
            w-72 sm:w-80 border-r overflow-y-auto
            transform lg:transform-none transition-transform duration-300 ease-in-out
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200'}
            pt-14 sm:pt-16 lg:pt-0
          `}
        >
          {/* Mobile close button */}
          <div className={`lg:hidden flex items-center justify-between px-4 py-3 border-b ${
            isDark ? 'border-dark-700' : 'border-slate-200'
          }`}>
            <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Customize
            </span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Panel Tabs */}
          <div className={`flex border-b ${isDark ? 'border-dark-700' : 'border-slate-200'}`}>
            {[
              { id: 'themes', icon: Palette, label: 'Themes' },
              { id: 'typography', icon: Type, label: 'Typography' },
              { id: 'animations', icon: Sparkles, label: 'Animations' },
            ].map((panel) => (
              <button
                key={panel.id}
                onClick={() => setActivePanel(activePanel === panel.id ? null : panel.id as typeof activePanel)}
                className={`flex-1 py-3 px-2 text-xs font-medium flex flex-col items-center gap-1 transition-colors ${
                  activePanel === panel.id
                    ? 'text-purple-500 border-b-2 border-purple-500'
                    : isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <panel.icon className="w-4 h-4" />
                {panel.label}
              </button>
            ))}
          </div>

          {/* Theme Hub Panel */}
          <AnimatePresence mode="wait">
            {activePanel === 'themes' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Theme Hub
                  </h3>
                  <span className="text-xs text-purple-500 font-medium">
                    {PRESENTATION_CREDITS.THEME_CHANGE} credits
                  </span>
                </div>

                {/* AI Theme Generator Button */}
                <button
                  onClick={() => setShowAIThemeModal(true)}
                  className={`w-full mb-4 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                    isDark
                      ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-purple-400 hover:from-purple-600/30 hover:to-cyan-600/30'
                      : 'bg-gradient-to-r from-purple-50 to-cyan-50 border border-purple-200 text-purple-600 hover:from-purple-100 hover:to-cyan-100'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Theme with AI ({PRESENTATION_CREDITS.AI_THEME_GENERATION} credits)
                </button>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {THEME_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        selectedCategory === cat.id
                          ? 'bg-purple-500 text-white'
                          : isDark ? 'bg-dark-700 text-text-secondary hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                {/* Theme Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {filteredThemes.map((theme) => (
                    <button
                      key={theme.id}
                      onClick={() => setPreviewTheme(theme.id)}
                      onDoubleClick={() => handleApplyTheme(theme.id)}
                      className={`relative rounded-xl overflow-hidden transition-all ${
                        (previewTheme === theme.id || currentPresentation.customization?.themePreset === theme.id)
                          ? 'ring-2 ring-purple-500 scale-[1.02]'
                          : 'hover:scale-[1.02]'
                      }`}
                    >
                      {/* Theme Preview */}
                      <div
                        className="h-24 p-3"
                        style={{ background: theme.colors.background }}
                      >
                        <div
                          className="w-full h-2 rounded mb-2"
                          style={{ background: theme.colors.primary }}
                        />
                        <div
                          className="w-3/4 h-1.5 rounded mb-1"
                          style={{ background: theme.colors.text, opacity: 0.6 }}
                        />
                        <div
                          className="w-1/2 h-1.5 rounded"
                          style={{ background: theme.colors.text, opacity: 0.4 }}
                        />
                      </div>
                      <div className={`p-2 text-left ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                        <p className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {theme.name}
                        </p>
                      </div>
                      {currentPresentation.customization?.themePreset === theme.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Apply Button */}
                {previewTheme && previewTheme !== currentPresentation.customization?.themePreset && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => handleApplyTheme(previewTheme)}
                    disabled={isSaving}
                    className="w-full mt-4 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                  >
                    {isSaving ? 'Applying...' : `Apply Theme (${PRESENTATION_CREDITS.THEME_CHANGE} credits)`}
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* Typography Panel */}
            {activePanel === 'typography' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Typography
                  </h3>
                  <span className="text-xs text-purple-500 font-medium">
                    {PRESENTATION_CREDITS.TYPOGRAPHY_CHANGE} credits
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Heading Font */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Heading Font
                    </label>
                    <select
                      value={tempTypography.headingFont}
                      onChange={(e) => setTempTypography({ ...tempTypography, headingFont: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      {AVAILABLE_FONTS.map((font) => (
                        <option key={font.id} value={font.id}>{font.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Body Font */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Body Font
                    </label>
                    <select
                      value={tempTypography.bodyFont}
                      onChange={(e) => setTempTypography({ ...tempTypography, bodyFont: e.target.value })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      {AVAILABLE_FONTS.map((font) => (
                        <option key={font.id} value={font.id}>{font.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Heading Size */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Heading Size
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => (
                        <button
                          key={size}
                          onClick={() => setTempTypography({ ...tempTypography, headingSize: size })}
                          className={`py-2 rounded-lg text-xs font-medium capitalize ${
                            tempTypography.headingSize === size
                              ? 'bg-purple-500 text-white'
                              : isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {size === 'xlarge' ? 'XL' : size[0].toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Heading Weight */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Heading Weight
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(['normal', 'medium', 'bold', 'extrabold'] as const).map((weight) => (
                        <button
                          key={weight}
                          onClick={() => setTempTypography({ ...tempTypography, headingWeight: weight })}
                          className={`py-2 rounded-lg text-xs font-medium capitalize ${
                            tempTypography.headingWeight === weight
                              ? 'bg-purple-500 text-white'
                              : isDark ? 'bg-dark-700 text-text-secondary' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {weight === 'extrabold' ? 'XB' : weight[0].toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleApplyTypography}
                    disabled={isSaving}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                  >
                    {isSaving ? 'Applying...' : `Apply Typography (${PRESENTATION_CREDITS.TYPOGRAPHY_CHANGE} credits)`}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Animations Panel */}
            {activePanel === 'animations' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Animations
                  </h3>
                  <span className="text-xs text-purple-500 font-medium">
                    {PRESENTATION_CREDITS.ANIMATION_CHANGE} credits
                  </span>
                </div>

                <div className="space-y-4">
                  {/* Slide Transition */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Slide Transition
                    </label>
                    <select
                      value={tempAnimations.slideTransition}
                      onChange={(e) => setTempAnimations({ ...tempAnimations, slideTransition: e.target.value as AnimationSettings['slideTransition'] })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      {SLIDE_TRANSITIONS.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Transition Duration */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Transition Duration: {tempAnimations.slideTransitionDuration}ms
                    </label>
                    <input
                      type="range"
                      min="200"
                      max="1500"
                      step="100"
                      value={tempAnimations.slideTransitionDuration}
                      onChange={(e) => setTempAnimations({ ...tempAnimations, slideTransitionDuration: parseInt(e.target.value) })}
                      className="w-full accent-purple-500"
                    />
                  </div>

                  {/* Element Animation */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Element Animation
                    </label>
                    <select
                      value={tempAnimations.elementAnimation}
                      onChange={(e) => setTempAnimations({ ...tempAnimations, elementAnimation: e.target.value as AnimationSettings['elementAnimation'] })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      {ELEMENT_ANIMATIONS.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Text Animation */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      Text Animation
                    </label>
                    <select
                      value={tempAnimations.textAnimation}
                      onChange={(e) => setTempAnimations({ ...tempAnimations, textAnimation: e.target.value as AnimationSettings['textAnimation'] })}
                      className={`w-full px-3 py-2 rounded-lg border text-sm ${
                        isDark
                          ? 'bg-dark-700 border-dark-600 text-white'
                          : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    >
                      {TEXT_ANIMATIONS.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleApplyAnimations}
                    disabled={isSaving}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                  >
                    {isSaving ? 'Applying...' : `Apply Animations (${PRESENTATION_CREDITS.ANIMATION_CHANGE} credits)`}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Slide Preview */}
          <div className="flex-1 p-2 sm:p-4 md:p-6 lg:p-8 flex items-center justify-center overflow-hidden" style={{ perspective: '1000px' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedSlideIndex}
                initial={getSlideTransitionInitial(tempAnimations.slideTransition)}
                animate={getSlideTransitionAnimate(tempAnimations.slideTransition)}
                exit={getSlideTransitionExit(tempAnimations.slideTransition)}
                transition={{ duration: tempAnimations.slideTransitionDuration / 1000, ease: 'easeInOut' }}
                className="w-full max-w-4xl aspect-video rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl sm:shadow-2xl overflow-hidden"
                style={{
                  background: currentSlide?.backgroundColor || activeTheme?.colors.background || '#0f0f1a',
                  transformStyle: 'preserve-3d',
                }}
              >
                {currentSlide && activeTheme ? (
                  <SlideLayoutRenderer
                    slide={currentSlide}
                    theme={activeTheme}
                    typography={tempTypography}
                    animations={tempAnimations}
                    isFullscreen={false}
                  />
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <p className="text-white/50">No slide selected</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom Slide Navigator */}
          <div className={`h-24 sm:h-28 md:h-32 border-t flex items-center gap-1 sm:gap-2 md:gap-4 px-2 sm:px-4 ${
            isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-slate-200'
          }`}>
            {/* Previous button - hidden on small mobile */}
            <button
              onClick={() => selectSlide(Math.max(0, selectedSlideIndex - 1))}
              disabled={selectedSlideIndex === 0}
              className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 hidden sm:flex ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              } disabled:opacity-30`}
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="flex-1 flex gap-2 sm:gap-3 overflow-x-auto py-2 scrollbar-hide">
              {/* Add Slide Button at start - smaller on mobile */}
              <button
                onClick={() => addSlide(-1)}
                className={`flex-shrink-0 w-8 sm:w-10 h-14 sm:h-16 md:h-20 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${
                  isDark
                    ? 'border-dark-600 hover:border-purple-500 text-text-muted hover:text-purple-500'
                    : 'border-slate-300 hover:border-purple-500 text-slate-400 hover:text-purple-500'
                }`}
                title="Add slide at beginning"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {currentPresentation.slides?.map((slide, index) => (
                <div
                  key={index}
                  className={`relative flex-shrink-0 w-24 sm:w-28 md:w-32 h-14 sm:h-16 md:h-20 rounded-lg overflow-hidden transition-all group cursor-pointer ${
                    selectedSlideIndex === index
                      ? 'ring-2 ring-purple-500 scale-105'
                      : 'hover:ring-1 hover:ring-purple-500/50'
                  }`}
                  style={{ background: slide.backgroundColor || activeTheme?.colors.background || '#0f0f1a' }}
                  onClick={() => selectSlide(index)}
                >
                  <div className="w-full h-full p-1.5 sm:p-2 text-left">
                    <div
                      className="text-[7px] sm:text-[8px] font-bold truncate"
                      style={{ color: activeTheme?.colors.primary || '#8b5cf6' }}
                    >
                      {slide.title}
                    </div>
                    <div
                      className="text-[5px] sm:text-[6px] mt-0.5 sm:mt-1 line-clamp-2"
                      style={{ color: activeTheme?.colors.text || '#ffffff', opacity: 0.7 }}
                    >
                      {slide.content?.[0]}
                    </div>
                  </div>

                  {/* Slide Actions - Hover Overlay (hidden on touch/mobile) */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:flex items-center justify-center gap-0.5 sm:gap-1">
                    {/* Move Left */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index > 0) moveSlide(index, index - 1);
                      }}
                      disabled={index === 0}
                      className="p-0.5 sm:p-1 rounded bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move left"
                    >
                      <ChevronLeft className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                    {/* Duplicate */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateSlide(index);
                      }}
                      className="p-0.5 sm:p-1 rounded bg-white/20 text-white hover:bg-white/30"
                      title="Duplicate slide"
                    >
                      <CopyIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                    {/* Delete */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSlide(index);
                      }}
                      disabled={currentPresentation.slides.length <= 1}
                      className="p-0.5 sm:p-1 rounded bg-red-500/50 text-white hover:bg-red-500/70 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Delete slide"
                    >
                      <Trash2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                    {/* Move Right */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (index < currentPresentation.slides.length - 1) moveSlide(index, index + 1);
                      }}
                      disabled={index === currentPresentation.slides.length - 1}
                      className="p-0.5 sm:p-1 rounded bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move right"
                    >
                      <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    </button>
                  </div>

                  {/* Settings Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSlideIndex(index);
                    }}
                    className="absolute top-0.5 sm:top-1 right-0.5 sm:right-1 p-0.5 sm:p-1 rounded bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    title="Slide Settings"
                  >
                    <Settings className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                  </button>
                  {/* Custom indicator */}
                  {(slide.backgroundColor || slide.layout || slide.transition || slide.animation) && (
                    <div className="absolute bottom-0.5 sm:bottom-1 right-0.5 sm:right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-purple-500 z-10" title="Has custom settings" />
                  )}
                </div>
              ))}

              {/* Add Slide Button at end */}
              <button
                onClick={() => addSlide(currentPresentation.slides.length - 1)}
                className={`flex-shrink-0 w-8 sm:w-10 h-14 sm:h-16 md:h-20 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${
                  isDark
                    ? 'border-dark-600 hover:border-purple-500 text-text-muted hover:text-purple-500'
                    : 'border-slate-300 hover:border-purple-500 text-slate-400 hover:text-purple-500'
                }`}
                title="Add slide at end"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Next button - hidden on small mobile */}
            <button
              onClick={() => selectSlide(Math.min((currentPresentation.slides?.length || 1) - 1, selectedSlideIndex + 1))}
              disabled={selectedSlideIndex >= (currentPresentation.slides?.length || 1) - 1}
              className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 hidden sm:flex ${
                isDark ? 'hover:bg-dark-700 text-text-secondary' : 'hover:bg-slate-100 text-slate-600'
              } disabled:opacity-30`}
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            {/* Slide Counter */}
            <div className={`flex-shrink-0 text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              {selectedSlideIndex + 1}/{currentPresentation.slides?.length || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Credit Confirmation Modal */}
      <AnimatePresence>
        {showCreditConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreditConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl p-6 ${
                isDark ? 'bg-dark-800' : 'bg-white'
              }`}
            >
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Confirm Action
              </h3>
              <p className={`mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                {showCreditConfirm.description}
              </p>

              <div className={`p-4 rounded-xl mb-4 ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>Cost</span>
                  <span className="text-purple-500 font-semibold">{showCreditConfirm.credits} credits</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>Your balance</span>
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>{user?.credits_remaining || 0} credits</span>
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? '#374151' : '#e2e8f0' }}>
                  <span className={isDark ? 'text-text-secondary' : 'text-slate-600'}>After</span>
                  <span className={isDark ? 'text-white' : 'text-slate-900'}>
                    {Math.max(0, (user?.credits_remaining || 0) - showCreditConfirm.credits)} credits
                  </span>
                </div>
              </div>

              {(user?.credits_remaining || 0) < showCreditConfirm.credits && (
                <p className="text-red-500 text-sm mb-4">
                  Insufficient credits. You need {showCreditConfirm.credits} credits.
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreditConfirm(null)}
                  className={`flex-1 py-2.5 rounded-xl font-medium ${
                    isDark ? 'bg-dark-700 text-white hover:bg-dark-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  disabled={isSaving || (user?.credits_remaining || 0) < showCreditConfirm.credits}
                  className="flex-1 py-2.5 bg-gradient-to-r from-purple-500 to-cyan-500 text-white rounded-xl font-medium disabled:opacity-50"
                >
                  {isSaving ? 'Applying...' : 'Confirm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Toast */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50"
          >
            <span>{error}</span>
            <button onClick={() => setError(null)}>
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Preview Mode */}
      <AnimatePresence>
        {isPreviewMode && activeTheme && (
          <FullscreenPresenter
            presentation={currentPresentation}
            theme={activeTheme}
            typography={tempTypography}
            animations={tempAnimations}
            initialSlide={selectedSlideIndex}
            onClose={() => setIsPreviewMode(false)}
          />
        )}
      </AnimatePresence>

      {/* AI Theme Generation Modal */}
      <AIThemeModal
        isOpen={showAIThemeModal}
        onClose={() => setShowAIThemeModal(false)}
        onApply={(generatedTheme) => {
          // Apply the generated theme directly without additional credit charge
          // The credits were already deducted during generation
          setPreviewTheme(null);
          // Update the presentation store with the custom theme
          if (currentPresentation) {
            const updatedData = {
              ...currentPresentation,
              customization: {
                ...currentPresentation.customization,
                themePreset: generatedTheme.id,
                customColors: generatedTheme.colors,
                typography: {
                  ...(currentPresentation.customization?.typography || {
                    headingFont: 'Inter',
                    bodyFont: 'Inter',
                    headingSize: 'large' as const,
                    bodySize: 'medium' as const,
                    headingWeight: 'bold' as const,
                    lineHeight: 'normal' as const,
                  }),
                  headingFont: generatedTheme.fonts.heading,
                  bodyFont: generatedTheme.fonts.body,
                },
                animations: currentPresentation.customization?.animations || {
                  slideTransition: 'fade' as const,
                  slideTransitionDuration: 500,
                  elementAnimation: 'fade-in' as const,
                  textAnimation: 'none' as const,
                  autoAdvance: false,
                  autoAdvanceDelay: 5,
                },
              },
            };
            setPresentation(updatedData, presentationId || '');
            // Also save to server
            fetch(`${API_URL}/presentations/${presentationId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ data: updatedData }),
            });
          }
        }}
        isDark={isDark}
        presentationId={presentationId || ''}
      />

      {/* Slide Settings Panel */}
      {editingSlideIndex !== null && currentPresentation.slides?.[editingSlideIndex] && activeTheme && (
        <SlideSettingsPanel
          isOpen={true}
          onClose={() => setEditingSlideIndex(null)}
          slide={currentPresentation.slides[editingSlideIndex]}
          slideIndex={editingSlideIndex}
          globalTheme={activeTheme}
          onUpdate={(slideIndex, updates) => {
            if (!currentPresentation.slides) return;
            const updatedSlides = [...currentPresentation.slides];
            updatedSlides[slideIndex] = { ...updatedSlides[slideIndex], ...updates };
            const updatedData = { ...currentPresentation, slides: updatedSlides };
            setPresentation(updatedData, presentationId || '');
            // Save to server
            fetch(`${API_URL}/presentations/${presentationId}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ data: updatedData }),
            });
          }}
          isDark={isDark}
        />
      )}
    </div>
  );
}
